/**
 * Facturas emitidas de Operaciones.
 * Base, IVA, suplido y cobrado van por separado. El histórico AIP de tienda se lista junto a AIP-T.
 */

import { useMemo, useState } from 'react';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { Download, Loader2, Search } from 'lucide-react';
import { downloadLegalInvoicePdf } from './hooks/useLegalInvoices';
import { ExportOperationsButton } from '@/pages/operations/ExportOperationsButton';
import { fmtEur, lineLabel, PeriodFilter, useOperationsPeriod } from '@/pages/operations/format';
import { useEmittedInvoices } from '@/pages/operations/hooks/useOperations';

const LINE_TABS: { value: string; label: string }[] = [
    { value: '', label: 'Todas' },
    { value: 'subscription', label: 'Suscripciones' },
    { value: 'license', label: 'Licencias' },
    { value: 'store', label: 'Tienda' },
];

function fmtDate(iso: string): string {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

export function InvoicesPage() {
    const { from, to, setPeriod } = useOperationsPeriod();
    const [line, setLine] = useState('');
    const [search, setSearch] = useState('');
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const { data: invoices, isLoading, error } = useEmittedInvoices(from, to, line, search);

    const totals = useMemo(() => {
        const rows = invoices ?? [];
        return rows.reduce(
            (acc, inv) => ({
                count: acc.count + 1,
                base: acc.base + inv.incomeBaseCents,
                vat: acc.vat + inv.vatCents,
                suplido: acc.suplido + inv.suplidoCents,
                collected: acc.collected + inv.collectedCents,
            }),
            { count: 0, base: 0, vat: 0, suplido: 0, collected: 0 }
        );
    }, [invoices]);

    const handleDownload = async (id: string, invoiceNumber: string) => {
        setDownloadingId(id);
        try {
            await downloadLegalInvoicePdf(id, invoiceNumber);
        } finally {
            setDownloadingId(null);
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Facturas emitidas"
                description="Lo cobrado al cliente no es el ingreso. En licencias la tasa es suplido y queda fuera de la base. Tienda nueva usa la serie AIP-T; las ya emitidas siguen en AIP."
            />

            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex flex-wrap items-end gap-3">
                    <PeriodFilter from={from} to={to} onChange={setPeriod} />
                    <ExportOperationsButton from={from} to={to} />
                </div>
                <div className="flex flex-wrap gap-2">
                    {LINE_TABS.map((t) => (
                        <button
                            key={t.value || 'all'}
                            type="button"
                            onClick={() => setLine(t.value)}
                            className={cn(
                                'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                                line === t.value
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                    type="search"
                    placeholder="Nº factura, email, pedido…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 rounded-lg bg-muted/30 border border-border text-sm"
                />
            </div>

            {error && (
                <p className="text-sm text-destructive">
                    {(error as Error).message}. Hace falta desplegar la API de operaciones.
                </p>
            )}

            <div className="rounded-xl border border-border bg-card/40 p-4 flex flex-wrap gap-6 text-sm">
                <div>
                    <span className="text-muted-foreground">Facturas: </span>
                    <span className="font-semibold">{totals.count}</span>
                </div>
                <div>
                    <span className="text-muted-foreground">Ingreso (base): </span>
                    <span className="font-semibold">{fmtEur(totals.base)}</span>
                </div>
                <div>
                    <span className="text-muted-foreground">IVA: </span>
                    <span className="font-semibold">{fmtEur(totals.vat)}</span>
                </div>
                <div>
                    <span className="text-muted-foreground">Suplido: </span>
                    <span className="font-semibold">{fmtEur(totals.suplido)}</span>
                </div>
                <div>
                    <span className="text-muted-foreground">Cobrado: </span>
                    <span className="font-semibold">{fmtEur(totals.collected)}</span>
                </div>
            </div>

            <div className="rounded-xl border border-border overflow-x-auto">
                <table className="w-full text-sm min-w-[960px]">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Nº factura</th>
                            <th className="px-4 py-3">Fecha</th>
                            <th className="px-4 py-3">Cliente</th>
                            <th className="px-4 py-3">Línea</th>
                            <th className="px-4 py-3 text-right">Base</th>
                            <th className="px-4 py-3 text-right">IVA</th>
                            <th className="px-4 py-3 text-right">Suplido</th>
                            <th className="px-4 py-3 text-right">Cobrado</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {isLoading && (
                            <tr>
                                <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                                    Cargando…
                                </td>
                            </tr>
                        )}
                        {!isLoading && (!invoices || invoices.length === 0) && !error && (
                            <tr>
                                <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                                    No hay facturas emitidas en este periodo.
                                </td>
                            </tr>
                        )}
                        {invoices?.map((inv) => (
                            <tr key={inv.id} className="hover:bg-muted/20">
                                <td className="px-4 py-3">
                                    <div className="font-mono text-xs font-semibold">{inv.invoiceNumber}</div>
                                    {inv.documentType === 'credit_note' && (
                                        <div className="text-[11px] text-muted-foreground">Rectificativa</div>
                                    )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">{fmtDate(inv.issuedAt)}</td>
                                <td className="px-4 py-3">
                                    <div className="font-medium truncate max-w-[180px]">{inv.customerName}</div>
                                    {inv.customerTaxId && (
                                        <div className="text-xs font-mono text-muted-foreground">{inv.customerTaxId}</div>
                                    )}
                                    <div className="text-xs text-muted-foreground truncate max-w-[180px]">{inv.customerEmail}</div>
                                </td>
                                <td className="px-4 py-3 text-xs">
                                    <div>{lineLabel(inv.businessLine)}</div>
                                    <div className="text-muted-foreground">{inv.series}</div>
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">{fmtEur(inv.incomeBaseCents)}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">{fmtEur(inv.vatCents)}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">{fmtEur(inv.suplidoCents)}</td>
                                <td className="px-4 py-3 text-right font-medium whitespace-nowrap">{fmtEur(inv.collectedCents)}</td>
                                <td className="px-4 py-3 text-right">
                                    <button
                                        type="button"
                                        disabled={downloadingId === inv.id}
                                        onClick={() => handleDownload(inv.id, inv.invoiceNumber)}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50"
                                    >
                                        {downloadingId === inv.id ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <Download className="h-3.5 w-3.5" />
                                        )}
                                        PDF
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
